import { PenLine, ArrowRightLeft, Plus, SearchX, EllipsisVertical, InfoIcon, Trash2Icon } from 'lucide-react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useSuspenseQuery } from '@apollo/client/react';
import { useMemo, useState, useTransition } from 'react';
import { debounce } from 'lodash';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';
import { UserRoleEnum, UserColumnsEnum, type IUserPolicy } from 'src/graphql/schema';
import { CompanyDocument } from 'src/graphql/actions/company/company.query';
import { ProfileDocument } from 'src/graphql/actions/profile/profile.query';
import { UsersDocument } from 'src/graphql/actions/user/users.query';
import { withSuspense } from 'src/hoc';
import { USERS_LIMIT } from 'src/shared/constants';
import { SimplePagination } from '@/components/ui/simple-pagination';
import { roleOptionsDict } from 'src/components/User';
import { InputSearch } from '@/components/ui/input-search';
import { EmptyBlock } from '@/components/ui/empty-block';
import {
  Select,
  SelectClose,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
} from '@/components/ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription } from 'src/components/ui/alert';

import { useUsers } from '../model';

import CardHeaderWithAction from './CardHeaderWithAction';
import PermissionPopover from './PermissionPopover';

type IUserRow = {
  name: string;
  email: string;
  phone?: string | null;
  role: UserRoleEnum;
  action: {
    id: string;
    policy: IUserPolicy;
  };
};

const ActionCell = ({ id, policy }: IUserRow['action']) => {
  const {
    ui: { openEditDialog, openRemoveDialog, openTransferOwnershipDialog },
  } = useUsers();

  const onEdit = () => openEditDialog(id);
  const onRemove = () => openRemoveDialog(id);

  if ([policy.remove, policy.transferOwnership, policy.update].some(Boolean))
    return (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end" className="w-81">
          {policy.update && (
            <DropdownMenuItem className="py-2 cursor-pointer" onClick={onEdit}>
              <PenLine />
              Edit
            </DropdownMenuItem>
          )}
          {policy.transferOwnership && (
            <DropdownMenuItem className="py-2 cursor-pointer" onClick={openTransferOwnershipDialog}>
              <ArrowRightLeft />
              Transfer ownership
            </DropdownMenuItem>
          )}
          {policy.remove && (
            <DropdownMenuItem
              className="py-2 cursor-pointer text-destructive focus:text-destructive!"
              onClick={onRemove}
            >
              <Trash2Icon />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );

  return null;
};

const FilterRoleSelect = ({
  value,
  onChange,
  onClear,
}: {
  value?: string;
  onChange: (role: UserRoleEnum) => void;
  onClear: () => void;
}) => {
  return (
    <Select onValueChange={onChange} value={value ?? ''}>
      <div className="relative">
        <SelectTrigger className="w-full sm:w-50" withCloseButton={Boolean(value)}>
          <SelectValue placeholder="Filter by role" />
        </SelectTrigger>
        <SelectClose isVisible={Boolean(value)} onClose={onClear} />
      </div>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Roles</SelectLabel>
          <SelectItem value={UserRoleEnum.Owner}>Account owner</SelectItem>
          <SelectItem value={UserRoleEnum.Admin}>Admin</SelectItem>
          <SelectItem value={UserRoleEnum.User}>User</SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const columnHelper = createColumnHelper<IUserRow>();
const columns = [
  columnHelper.display({
    header: 'Name',
    cell: ({ row }) => {
      const { name, email } = row.original;
      const fallback = name.charAt(0).toUpperCase();

      return (
        <div className="flex items-center gap-2">
          <Avatar className="size-8 rounded-full">
            <AvatarImage src={getGravatarUrl(email)} />
            <AvatarFallback className="rounded-full bg-primary text-white">{fallback}</AvatarFallback>
          </Avatar>
          <div className="text-sm">
            <span className="text-foreground font-medium">{name}</span>
            <div className="text-muted-foreground">{email}</div>
          </div>
        </div>
      );
    },
    meta: {
      headerClassName: 'min-w-60 w-full',
      cellClassName: 'p-2 min-w-60 w-full',
    },
  }),
  columnHelper.accessor('phone', {
    header: 'Phone',
    cell: (props) => <div className="text-sm truncate">{props.getValue()}</div>,
    meta: {
      headerClassName: 'min-w-60 max-w-60',
      cellClassName: 'min-w-60 max-w-60',
    },
  }),
  columnHelper.accessor('role', {
    header: () => <div className="flex items-center gap-1.5">Role</div>,
    cell: (props) => {
      const { badgeVariant, label } = roleOptionsDict[props.getValue()];

      return (
        <Badge className="whitespace-nowrap" variant={badgeVariant}>
          {label}
        </Badge>
      );
    },
    meta: {
      headerClassName: 'min-w-29.5',
      cellClassName: 'min-w-29.5',
    },
  }),
  columnHelper.accessor('action', {
    header: '',
    cell: (props) => <ActionCell {...props.getValue()} />,
    meta: {
      headerClassName: 'w-14',
      cellClassName: 'w-14 p-2',
    },
  }),
];

const UsersCard = () => {
  const [{ page, searchQuery, role }, setFilters] = useState<{
    page: number;
    searchQuery: string;
    role?: UserRoleEnum;
  }>({ page: 1, searchQuery: '', role: undefined });
  const [isPending, startTransition] = useTransition();
  const {
    data: {
      profile: { id: profileId },
    },
  } = useSuspenseQuery(ProfileDocument);
  const {
    data: { company },
  } = useSuspenseQuery(CompanyDocument);
  const {
    data: { users },
  } = useSuspenseQuery(UsersDocument, {
    variables: {
      page,
      perPage: USERS_LIMIT,
      orderBy: UserColumnsEnum.Role,
      queryFilter: searchQuery,
      ...(role && { role }),
    },
    fetchPolicy: 'cache-and-network',
  });
  const {
    ui: { openAddDialog },
  } = useUsers();
  const usersMapped = useMemo(
    () =>
      users.nodes.map((user) => {
        const isCurrentUserOwner = user.id === profileId && user.role === UserRoleEnum.Owner;

        return {
          name: user.name || '-',
          email: user.email,
          role: user.role,
          phone: user.phone,
          action: {
            id: user.id,
            policy: { ...user.policy, transferOwnership: isCurrentUserOwner },
          },
        };
      }),
    [users, profileId],
  );
  const table = useReactTable({
    data: usersMapped,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const onPageChange = (page: number) => startTransition(() => setFilters((prev) => ({ ...prev, page })));
  const onPrevious = () => onPageChange(page - 1);
  const onNext = () => onPageChange(page + 1);

  const onSearch = useMemo(
    () =>
      debounce(
        (query: string) => startTransition(() => setFilters((prev) => ({ ...prev, page: 1, searchQuery: query }))),
        500,
      ),
    [],
  );
  const onClearSearch = () => setFilters((prev) => ({ ...prev, page: 1, searchQuery: '' }));

  const onFilterRoleChange = (role: UserRoleEnum) =>
    startTransition(() => setFilters((prevFilters) => ({ ...prevFilters, page: 1, role })));
  const clearFilterRole = () => setFilters((prevFilters) => ({ ...prevFilters, page: 1, role: undefined }));

  return (
    <Card>
      <CardHeaderWithAction
        title="Organization members"
        description={`${company.policy.update ? 'Manage' : 'See'} who has access to your organization.`}
        action={
          <div className="flex flex-col sm:flex-row gap-2">
            <PermissionPopover />
            {company.policy.createUser && (
              <Button onClick={openAddDialog} size="sm">
                <Plus /> Add members
              </Button>
            )}
          </div>
        }
      />
      <CardContent className="space-y-6">
        {company.ssoEnabled && company.policy.update && (
          <Alert className="bg-accent border-card-foreground has-[>svg]:gap-x-1.5">
            <InfoIcon />
            <AlertDescription>
              Your members are managed through your SSO provider. To add members, go to your SSO provider and add them
              there. Their roles can then be managed directly in Codekeeper.
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <FilterRoleSelect value={role} onChange={onFilterRoleChange} onClear={clearFilterRole} />
          <InputSearch
            placeholder="Search"
            onChange={(e) => onSearch(e.target.value)}
            isLoading={isPending}
            onClear={onClearSearch}
            className="sm:w-93.5 max-w-full"
          />
        </div>
        <div className="border border-border rounded-md text-foreground bg-white overflow-hidden">
          <Table isLoading={isPending}>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'bg-gray-50 text-slate-700 font-bold text-xs first:rounded-tl-md last:rounded-tr-md uppercase',
                        header.column.columnDef.meta?.headerClassName,
                      )}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="group/row">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          cell.column.columnDef.meta?.cellClassName,
                          'group-last/row:first:rounded-bl-md group-last/row:last:rounded-br-md',
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={table.getAllColumns().length}>
                    <EmptyBlock
                      title="No results found"
                      description="There were no results for your search. Try another filter. "
                      icon={SearchX}
                      withCard={false}
                      classes={{
                        title: 'text-base',
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <SimplePagination
          page={page}
          totalPages={users.pagesCount}
          totalItems={users.nodesCount}
          limit={USERS_LIMIT}
          onPrevious={onPrevious}
          onNext={onNext}
          isPreviousDisabled={page <= 1}
          isNextDisabled={page >= users.pagesCount}
          hideOnSinglePage
          className="justify-end"
        />
      </CardContent>
    </Card>
  );
};

export default withSuspense(UsersCard);
